import { Select, MultiSelect, type SelectOption } from "@/components/ui/select"
import type { TicketHumanAssignee } from "@/types/ticket"

export type TicketsFilterMeta = {
  queues: { id: number; name: string }[]
  availableTags: { key: string; label: string }[]
  assignableUsers: TicketHumanAssignee[]
}

export type SlaBreachedKey = "fr" | "idle" | "res" | "any"

type Props = {
  filterMeta: TicketsFilterMeta
  // current selections (URL-driven)
  queue:       string  // "all" | "my" | "<id>"
  priority:    string[] // empty = no filter
  tags:        string[]
  status:      string[]
  assignedTo:  string  // "" | "me" | "none" | "<user_id>"
  // SLA breach deep-link filter — entry point lives on the SLA dashboard.
  // No UI control creates this filter from the bar; it just renders +
  // clears as a chip when the URL has the params set.
  slaBreached:      SlaBreachedKey | null
  slaBreachedSince: string | null  // e.g. "7d" / "30d"
  // change handlers
  onQueueChange:      (v: string) => void
  onPriorityChange:   (v: string[]) => void
  onTagsChange:       (v: string[]) => void
  onStatusChange:     (v: string[]) => void
  onAssignedToChange: (v: string) => void
  onClearSlaBreached: () => void
  onClear:            () => void
}

const PRIORITY_OPTIONS: SelectOption<string>[] = [
  { value: "urgent", label: "Urgent" },
  { value: "high",   label: "High" },
  { value: "normal", label: "Normal" },
  { value: "low",    label: "Low" },
]

const STATUS_OPTIONS: SelectOption<string>[] = [
  { value: "open",             label: "Open" },
  { value: "pending",          label: "Pending" },
  { value: "pending_feedback", label: "Awaiting feedback" },
  { value: "imported",         label: "Imported" },
  { value: "closed",           label: "Closed" },
]

const SLA_BREACHED_LABELS: Record<SlaBreachedKey, string> = {
  fr:   "First-response breaches",
  idle: "Idle-response breaches",
  res:  "Resolution breaches",
  any:  "SLA breaches",
}

function describeSinceWindow(raw: string | null): string | null {
  if (!raw) return null
  const m = /^(\d+)d$/.exec(raw)
  if (!m) return null
  const days = Number(m[1])
  if (!Number.isFinite(days) || days <= 0) return null
  return `last ${days} day${days === 1 ? "" : "s"}`
}

export function TicketsFilterBar({
  filterMeta,
  queue,
  priority,
  tags,
  status,
  assignedTo,
  slaBreached,
  slaBreachedSince,
  onQueueChange,
  onPriorityChange,
  onTagsChange,
  onStatusChange,
  onAssignedToChange,
  onClearSlaBreached,
  onClear,
}: Props) {
  // Queue dropdown — "All queues" + "My queue" pseudo-values, then real queues.
  const queueOptions: SelectOption<string>[] = [
    { value: "all", label: "All queues" },
    { value: "my",  label: "My tickets" },
    ...filterMeta.queues.map((q) => ({ value: String(q.id), label: q.name })),
  ]
  const tagOptions: SelectOption<string>[] = filterMeta.availableTags.map((t) => ({
    value: t.key, label: t.label,
  }))
  const assigneeOptions: SelectOption<string>[] = [
    { value: "",     label: "All assignees" },
    { value: "me",   label: "Assigned to me" },
    { value: "none", label: "Unassigned" },
    ...filterMeta.assignableUsers.map((u) => ({
      value: String(u.id),
      label: u.name ?? u.email,
    })),
  ]

  const slaSinceLabel = describeSinceWindow(slaBreachedSince)
  const slaChipLabel = slaBreached
    ? slaSinceLabel
      ? `${SLA_BREACHED_LABELS[slaBreached]} · ${slaSinceLabel}`
      : SLA_BREACHED_LABELS[slaBreached]
    : null

  const hasAny =
    queue !== "all" ||
    priority.length > 0 ||
    tags.length > 0 ||
    status.length > 0 ||
    assignedTo !== "" ||
    slaBreached != null

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-card border border-border bg-app-panel p-3">
      <Field label="Queue">
        <Select
          value={queueOptions.find((o) => o.value === queue) ?? queueOptions[0]}
          options={queueOptions}
          onChange={(o) => onQueueChange(o?.value ?? "all")}
          isClearable={false}
        />
      </Field>
      <Field label="Priority">
        <MultiSelect
          value={PRIORITY_OPTIONS.filter((o) => priority.includes(o.value))}
          options={PRIORITY_OPTIONS}
          onChange={(opts) => onPriorityChange(opts.map((o) => o.value))}
          placeholder="All priorities"
          closeMenuOnSelect={false}
        />
      </Field>
      <Field label="Tags">
        <MultiSelect
          value={tagOptions.filter((o) => tags.includes(o.value))}
          options={tagOptions}
          onChange={(opts) => onTagsChange(opts.map((o) => o.value))}
          placeholder="All tags"
          closeMenuOnSelect={false}
        />
      </Field>
      <Field label="Status">
        <MultiSelect
          value={STATUS_OPTIONS.filter((o) => status.includes(o.value))}
          options={STATUS_OPTIONS}
          onChange={(opts) => onStatusChange(opts.map((o) => o.value))}
          placeholder="All status"
          closeMenuOnSelect={false}
        />
      </Field>
      <Field label="Assigned to">
        <Select
          value={assigneeOptions.find((o) => o.value === assignedTo) ?? assigneeOptions[0]}
          options={assigneeOptions}
          onChange={(o) => onAssignedToChange(o?.value ?? "")}
          isClearable={false}
        />
      </Field>

      {/* SLA breach deep-link chip. Created by the SLA dashboard's
          "View breaches" links — no UI here to set it; just clear it. */}
      {slaChipLabel && (
        <span
          className="inline-flex h-9 items-center gap-1 rounded-control border border-border bg-app-warning-soft px-3 text-xs text-app-text"
          title="SLA breach filter (from SLA dashboard)"
        >
          {slaChipLabel}
          <button
            type="button"
            onClick={onClearSlaBreached}
            aria-label="Clear SLA breach filter"
            className="-mr-1 ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full text-app-muted transition hover:bg-white/10 hover:text-app-text"
          >
            ×
          </button>
        </span>
      )}

      {/* Placeholder "More filters" button — desktop-only and disabled
          until wired up. Hidden below sm: (no value on mobile, where
          space is at a premium). */}
      <button
        type="button"
        disabled
        title="More filters (coming soon)"
        className="hidden h-9 items-center gap-1 rounded-control border border-border bg-white/5 px-3 text-xs text-app-muted opacity-60 sm:inline-flex"
      >
        ⛢ More filters
      </button>

      <button
        type="button"
        onClick={onClear}
        disabled={!hasAny}
        className="ml-auto text-xs text-app-muted underline-offset-2 transition hover:text-app-text disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:text-app-muted"
      >
        Clear
      </button>
    </div>
  )
}

// Mobile-first label/select stack. Label sits above the select on the
// narrowest phones; from sm: up they go inline (label left, select to
// the right). Each field claims a flexible minimum width so two fit per
// row at sm:, three at md:.
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex w-full flex-col gap-1 sm:w-auto sm:flex-row sm:items-center sm:gap-2">
      <span className="text-xs uppercase tracking-wide text-app-muted">{label}</span>
      <div className="w-full min-w-[160px] sm:w-auto">{children}</div>
    </div>
  )
}
